<template>
  <v-menu
    v-model="opened"
    :close-on-content-click="false"
    :offset="[10, 0]"
    :activator="mobile ? '#screen-left-anchor' : '#leave-button'"
    :location="mobile ? 'top left' : 'bottom right'"
    :width="mobile ? '100%' : '45%'"
    :height="mobile ? '100vh' : undefined"
    @afterEnter="afterOpenMenu"
    @afterLeave="afterCloseMenu"
  >
    <template v-slot:activator="{props}">
      <v-btn
        v-bind="props"
        class="button"
        active-color="primary"
        :active="opened"
        stacked
        density="compact"
        size="small"
        variant="flat"
        :rounded="4"
      >
        <template v-slot:prepend>
          <v-badge :model-value="hasUnread" dot color="#D83737" location="top right" bordered :dot-size="12">
            <v-icon icon="msf:chat_bubble" />
          </v-badge>
        </template>
        {{ $t('meeting.tools.chat') }}
      </v-btn>
    </template>
    <v-sheet :elevation="5" class="chat-menu">
      <v-sheet v-if="mobile" class="header">
        {{ $t('meeting.tools.chat') }}
        <v-icon-btn class="close" @click="opened = false" icon="close" rounded="circle" color="#DCDCDC" />
      </v-sheet>
      <div class="chat-content" ref="content">
        <div :class="['chat-item', message.author]" v-for="message in chat.read" :key="message.timestamp">
          <div class="timestamp">
            {{ formatTime(message.timestamp) }} <b>{{ message.fullName }}</b>
          </div>
          <div class="bubble">
            {{ message.text }}
          </div>
        </div>
        <div :class="['chat-item', message.author]" v-for="message in chat.unread" :key="message.timestamp">
          <div class="timestamp">
            {{ formatTime(message.timestamp) }} <b>{{ message.fullName }}</b>
          </div>
          <div class="bubble">
            {{ message.text }}
          </div>
        </div>
      </div>
      <div class="chat-input">
        <v-textarea
          :placeholder="$t('meeting.chat.inputPlaceholder')"
          :disabled="chat.isDisabledChat"
          variant="solo"
          hide-details
          autocomplete="off"
          v-model="text"
          rows="1"
          no-resize
          clearable
        >
          <template v-slot:append-inner>
            <v-icon-btn
              :disabled="!text || chat.isDisabledChat"
              @click="sendHandler"
              icon="msf:send"
              color="#007DA8"
              rounded="circle"
            ></v-icon-btn>
          </template>
        </v-textarea>
      </div>
    </v-sheet>
  </v-menu>
  <v-menu
    v-model="openNotification"
    :close-on-content-click="false"
    activator="#meeting-indicator"
    persistent
    :offset="[10, 0]"
    location="bottom right"
    :close-delay="0"
  >
    <v-sheet class="bubble-popup" @click="notificationClicked" :elevation="5">
      <v-sheet class="round-icon" rounded="circle" :width="40" :height="40">
        <v-icon :icon="notificationIcon" />
      </v-sheet>
      {{ notification?.text }}
    </v-sheet>
  </v-menu>
</template>
<script setup>
import {computed, ref} from 'vue'
import {useChatStore, useUserStore} from '@/stores'
import moment from 'moment'
import {useChat} from '@/procedures/useChat'
import {useDisplay} from 'vuetify'

const iconMap = {
  client: 'msf:person',
  assistant: 'msf:headset_mic',
  observer: 'msf:visibility',
}

const NOTIFICATION_TIMEOUT = 5000

const chat = useChatStore()
const user = useUserStore()
const chatProcedure = useChat()
const {platform, xs} = useDisplay()
const mobile = computed(() => (platform.value.android && platform.value.ios) || xs.value)
const opened = ref(false)
const text = ref('')
const content = ref(null)
const notification = ref(null)
const openNotification = computed({
  get: () => notification.value !== null,
  set: value => {
    if (!value) hideNotification()
  },
})
const notificationIcon = computed(() => iconMap[notification.value?.party] ?? iconMap.client)
let notificationTimer
let observer = null
const hasUnread = computed(() => !opened.value && chat.unread.length > 0)

const afterOpenMenu = () => {
  hideNotification()
  chat.markRead()
  const el = content.value
  if (el) {
    observer = new MutationObserver(() => {
      const container = el
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'instant',
        })
      }
    })
    observer.observe(el, {
      childList: true,
      subtree: true,
    })
    el.scrollTo({
      top: el.scrollHeight,
      behavior: 'instant',
    })
  }
}
const afterCloseMenu = () => {
  if (observer) {
    observer.disconnect()
  }
}

function hideNotification() {
  clearTimeout(notificationTimer)
  notification.value = null
}

function sendHandler() {
  if (!text.value.length) return
  const {id, fullName, email, party} = user
  chatProcedure.sendChat({text: text.value, id, fullName, email, party, timestamp: new Date().toISOString()})
  text.value = ''
}

chat.$onAction(({name, args, after}) => {
  if (name !== 'addMessage') return
  const [message] = args
  after(() => {
    if (opened.value) {
      chat.markRead()
    } else if (message.author === 'counterparty') {
      notification.value = message
      clearTimeout(notificationTimer)
      notificationTimer = setTimeout(hideNotification, NOTIFICATION_TIMEOUT)
    }
  })
})
const notificationClicked = () => {
  opened.value = true
  notification.value = null
}
const formatTime = value => moment(value).format('HH:mm')
</script>
<style lang="scss" scoped>
.bubble-popup {
  position: relative;
  overflow: visible !important;
  border-radius: $radius-rounded-8;
  padding: $spacing-m;
  display: flex;
  flex-direction: row;
  gap: $spacing-xxs;
  min-height: 72px;
  align-items: center;

  &::before {
    content: '';
    position: absolute;
    top: -8px;
    right: 20px;

    width: 0;
    height: 0;

    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid white;
  }
}
.chat-menu {
  display: flex;
  flex-direction: column;
  min-height: 300px;
  padding: $spacing-m;
  overflow: hidden;
  .header {
    flex-grow: 0;
    min-height: 42px;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-bottom: 17px;

    .close {
      position: absolute;
      right: 0;
    }
  }
  .chat-content {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;
    padding-bottom: $spacing-xs;
    overflow: auto;
    .chat-item {
      max-width: 90%;
      width: fit-content;
      .bubble {
        padding: $spacing-xs $spacing-s;
        border-radius: $radius-rounded-12;
        white-space: pre-wrap;
        word-break: break-word;
        overflow-wrap: break-word;
      }
      .timestamp {
        font-size: 0.8em;
        color: $font-gray-2;
      }
      &.counterparty {
        .bubble {
          background: $background-primary-subtle;
          border-top-left-radius: 0;
        }
        align-self: flex-start;
      }
      &.party {
        .bubble {
          background: $background-neutral-20;
          border-top-right-radius: 0;
        }
        align-self: flex-end;
      }
    }
  }
  .chat-input {
    flex-grow: 0;
    :deep(textarea) {
      resize: none !important;
    }
  }
}
.round-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: $background-neutral-20;
  gap: 8px;
}
</style>

<style lang="scss">
@import '@/scss/button-group';
</style>
